/*
 * cldrChar: encapsulate Survey Tool functions related to Unicode characters
 */

import { unicodeName } from "unicode-name";

import * as cldrEscaper from "./cldrEscaper.mjs";

/**
 * @param {String} s string to be tested
 * @returns true if the string should be displayed as a tag
 */
function containsTaggable(s) {
  for (let c of split(s)) {
    if (shouldDisplayAsTag(c)) {
      return true;
    }
  }
  return false;
}

/*
  For clarity, these characters are displayed as tags, but they are not included
  in the Insert menu. They are not invisibles, so the characters themselves, rather
  than their names, are displayed in the tags. Users can see their names by hovering.

  ◦	“ U+201C LEFT DOUBLE QUOTATION MARK
  ◦	” U+201D RIGHT DOUBLE QUOTATION MARK
  ◦	″ U+2033 DOUBLE PRIME
  ◦	ʼ U+02BC MODIFIER LETTER APOSTROPHE
  ◦	‘ U+2018 LEFT SINGLE QUOTATION MARK
  ◦	’ U+2019 RIGHT SINGLE QUOTATION MARK
  ◦	′ U+2032 PRIME
*/
const tagWithNoName = [0x201c, 0x201d, 0x2033, 0x02bc, 0x2018, 0x2019, 0x2032];

function shouldDisplayAsTag(s) {
  const c = firstChar(s);
  if (c == " ") {
    return false;
  }
  return isSpecial(c) || tagWithNoName.indexOf(firstCodePoint(c)) >= 0;
}

/**
 * Get the first code point in the given string
 *
 * @param {String} s the string
 * @returns {Number} the first code point
 */
function firstCodePoint(s) {
  // To support code points greater than U+FFFF, use codePointAt, NOT charCodeAt.
  return s.codePointAt(0);
}

/**
 * Get the first character in the given string
 *
 * @param {String} s the string
 * @returns {String} the first character
 */
function firstChar(s) {
  // To support code points greater than U+FFFF, use fromCodePoint/codePointAt, NOT fromCharCode/charCodeAt.
  return String.fromCodePoint(s.codePointAt(0));
}

/**
 * Split a string into an array of characters
 *
 * @param {String} s the string
 * @returns {Array} the array
 */
function split(s) {
  // To support codepoints greater than U+FFFF, use split(/(?:)/u), not split("")
  return s.split(/(?:)/u);
}

/**
 * Get the name for the given code point
 *
 * @param {Number} codePoint a code point such as 0x0020
 * @returns {String} the name such as "SPACE"
 */
function name(codePoint) {
  return unicodeName(codePoint);
}

/**
 * Get the "U+..." string representation for the given code point
 *
 * @param {Number} codePoint a code point such as 0x0020
 * @returns {String} the standard notation such as "U+0020"
 */
function uPlus(codePoint) {
  return "U+" + codePoint.toString(16).toUpperCase().padStart(4, "0");
}

/**
 * If the given string is valid "U+..." for a character code point, return that
 * character; otherwise return the string unchanged
 *
 * @param {String} s the string, maybe like "U+662F"
 * @returns {String} the converted string like "是", or the original string
 */
function fromUPlus(s) {
  if (s?.startsWith("U+")) {
    const codePoint = parseInt(s.slice(2), 16);
    if (isAllowed(codePoint)) {
      // To support codepoints greater than U+FFFF, use fromCodePoint, not fromCharCode
      return String.fromCodePoint(codePoint);
    }
  }
  return s;
}

function isAllowed(codePoint) {
  // Allow only standard code points, excluding private use and surrogates
  return (
    codePoint > 0 &&
    codePoint < 0x10ffff &&
    (codePoint & 0xffff) < 0xfffe &&
    (codePoint < 0xd800 || codePoint > 0xdfff)
  );
}

/**
 * Is the given character (single-character string) white space?
 *
 * @param {String} c -- the single-character string
 * @returns {Boolean} true or false
 */
function isWhiteSpace(c) {
  if (typeof c !== "string" || [...c].length !== 1) {
    throw new Error("isWhiteSpace requires a single-character string");
  }
  // Reference: https://util.unicode.org/UnicodeJsps/character.jsp
  return c.match(/\p{White_Space}/u);
}

function isSpecial(c) {
  // Caution: cldrEscaper requires the Survey Tool back end for full coverage. Without the back end,
  // as when running unit tests for the front end, cldrEscaper has a very limited set of fallback data.
  return Boolean(cldrEscaper.getShortName(c));
}

export {
  containsTaggable,
  firstChar,
  firstCodePoint,
  fromUPlus,
  isSpecial,
  isWhiteSpace,
  name,
  shouldDisplayAsTag,
  split,
  uPlus,
};
